// Frontend/geniac/src/Components/Settings/PaymentMethodsPage.tsx
// Page de gestion des moyens de paiement

import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FiAlertTriangle, FiX, FiCreditCard } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  listPaymentMethods,
  createSetupIntent,
  addPaymentMethod,
  setDefaultPaymentMethod,
  removePaymentMethod,
  PaymentMethod
} from '../../api/paymentMethods';

/**
 * Modal de confirmation moderne
 */
function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  cardInfo,
  loading,
  t
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  cardInfo: { brand: string; last4: string };
  loading: boolean;
  t: (key: string, options?: any) => string;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-in fade-in zoom-in duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FiAlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('paymentMethods.deleteModal.title')}
              </h3>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gray-100 rounded-lg p-4 flex items-center space-x-3">
                <FiCreditCard className="w-8 h-8 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900 capitalize">{cardInfo.brand}</p>
                  <p className="text-sm text-gray-500">•••• •••• •••• {cardInfo.last4}</p>
                </div>
              </div>
            </div>
            <p className="text-center text-gray-600">
              {t('paymentMethods.deleteModal.message')} {t('paymentMethods.deleteModal.warning')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 p-6 bg-gray-50 rounded-b-2xl">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50"
            >
              {t('paymentMethods.deleteModal.cancel')}
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>{t('paymentMethods.deleteModal.deleting')}</span>
                </>
              ) : (
                <span>{t('paymentMethods.deleteModal.delete')}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Initialiser Stripe avec la clé publique depuis .env
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

/**
 * Composant pour le formulaire d'ajout de carte (avec Stripe Elements)
 */
function AddCardForm({ onSuccess, onCancel, t }: { onSuccess: () => void; onCancel: () => void; t: (key: string, options?: any) => string }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    setLoading(true);

    try {
      // 1. Créer un Setup Intent
      const { client_secret } = await createSetupIntent();

      // 2. Confirmer le Setup Intent avec les informations de la carte
      const { error, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (setupIntent?.payment_method) {
        // 3. Enregistrer le payment method sur notre backend
        await addPaymentMethod(setupIntent.payment_method as string);
        toast.success(t('paymentMethods.toast.addSuccess'));
        onSuccess();
      }
    } catch (error: any) {
      console.error('Add card error:', error);
      toast.error(error.message || t('paymentMethods.toast.addError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('paymentMethods.addModal.cardDetails')}
        </label>
        <div className="bg-white p-3 rounded border border-gray-300">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {t('paymentMethods.addModal.cancel')}
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? t('paymentMethods.addModal.adding') : t('paymentMethods.addModal.add')}
        </button>
      </div>
    </form>
  );
}

/**
 * Composant pour afficher une carte de paiement
 */
function PaymentMethodCard({
  paymentMethod,
  onSetDefault,
  onRemove,
  onRequestDelete,
  t,
}: {
  paymentMethod: PaymentMethod;
  onSetDefault: () => void;
  onRemove: () => void;
  onRequestDelete: (pm: PaymentMethod) => void;
  t: (key: string, options?: any) => string;
}) {
  const [loading, setLoading] = useState(false);

  const handleSetDefault = async () => {
    if (paymentMethod.is_default) return;

    setLoading(true);
    try {
      await setDefaultPaymentMethod(paymentMethod.id);
      toast.success(t('paymentMethods.toast.setDefaultSuccess'));
      onSetDefault();
    } catch (error: any) {
      console.error('Set default error:', error);
      toast.error(t('paymentMethods.toast.setDefaultError'));
    } finally {
      setLoading(false);
    }
  };

  const getCardBrandLogo = (brand: string) => {
    const brandLower = brand.toLowerCase();
    if (brandLower === 'visa') {
      return (
        <svg className="h-8 w-12" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#1A1F71"/>
          <path d="M19.5 21H17L18.75 11H21.25L19.5 21Z" fill="white"/>
          <path d="M28 11L25.5 18L25.25 16.75L24.25 12.25C24.25 12.25 24.125 11 22.5 11H18.25L18.125 11.375C18.125 11.375 20 11.75 21.75 13L24 21H26.75L31 11H28Z" fill="white"/>
          <path d="M32 21L32.5 18.5H35.5L36 21H38.5L36 11H33.5C32.25 11 31.75 12 31.75 12L27.5 21H30L32 21ZM33.25 14L34.75 16.5H32.75L33.25 14Z" fill="white"/>
          <path d="M14.5 14.25L14.125 12.5C14.125 12.5 12.25 11.75 10 11.75L10.125 11H15.5C16.5 11 17 11.75 17 11.75L15.5 18.5L14.5 14.25Z" fill="white"/>
        </svg>
      );
    }
    if (brandLower === 'mastercard') {
      return (
        <svg className="h-8 w-12" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#000"/>
          <circle cx="19" cy="16" r="9" fill="#EB001B"/>
          <circle cx="29" cy="16" r="9" fill="#F79E1B"/>
          <path d="M24 9.5C26 11 27.5 13.25 27.5 16C27.5 18.75 26 21 24 22.5C22 21 20.5 18.75 20.5 16C20.5 13.25 22 11 24 9.5Z" fill="#FF5F00"/>
        </svg>
      );
    }
    if (brandLower === 'amex') {
      return (
        <svg className="h-8 w-12" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#006FCF"/>
          <path d="M10 16L12 12H16L14 16L16 20H12L10 16Z" fill="white"/>
          <path d="M18 12H24V14H20V15H24V17H20V18H24V20H18V12Z" fill="white"/>
          <path d="M26 12H30L32 15L34 12H38L34 16L38 20H34L32 17L30 20H26L30 16L26 12Z" fill="white"/>
        </svg>
      );
    }
    return <FiCreditCard className="h-8 w-12 text-gray-400" />;
  };

  const isExpired = () => {
    const now = new Date();
    const expDate = new Date(paymentMethod.card.exp_year, paymentMethod.card.exp_month - 1);
    return expDate < now;
  };

  return (
    <div className={`border rounded-xl p-5 transition-all duration-200 ${
      paymentMethod.is_default
        ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-white shadow-sm'
        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            {getCardBrandLogo(paymentMethod.card.brand)}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-gray-900 capitalize">
                {paymentMethod.card.brand}
              </span>
              <span className="text-gray-500 font-mono">•••• {paymentMethod.card.last4}</span>
              {paymentMethod.is_default && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {t('paymentMethods.card.default')}
                </span>
              )}
              {isExpired() && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  {t('paymentMethods.card.expired')}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              {t('paymentMethods.card.expires')} {paymentMethod.card.exp_month.toString().padStart(2, '0')}/{paymentMethod.card.exp_year}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!paymentMethod.is_default && (
            <button
              onClick={handleSetDefault}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
            >
              {t('paymentMethods.card.setDefault')}
            </button>
          )}
          <button
            onClick={() => onRequestDelete(paymentMethod)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {t('paymentMethods.card.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Page principale de gestion des moyens de paiement
 */
export default function PaymentMethodsPage() {
  const { t } = useTranslation('settings');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  // État pour le modal de suppression
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    paymentMethod: PaymentMethod | null;
    loading: boolean;
  }>({ isOpen: false, paymentMethod: null, loading: false });

  const loadPaymentMethods = async () => {
    setLoading(true);
    try {
      const methods = await listPaymentMethods();
      setPaymentMethods(methods);
    } catch (error) {
      console.error('Load payment methods error:', error);
      toast.error(t('paymentMethods.toast.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const handleAddSuccess = () => {
    setShowAddForm(false);
    loadPaymentMethods();
  };

  const handleRequestDelete = (pm: PaymentMethod) => {
    setDeleteModal({ isOpen: true, paymentMethod: pm, loading: false });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.paymentMethod) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      await removePaymentMethod(deleteModal.paymentMethod.id);
      toast.success(t('paymentMethods.toast.deleteSuccess'));
      setDeleteModal({ isOpen: false, paymentMethod: null, loading: false });
      loadPaymentMethods();
    } catch (error: any) {
      console.error('Remove error:', error);
      toast.error(error.response?.data?.message || t('paymentMethods.toast.deleteError'));
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCloseDeleteModal = () => {
    if (!deleteModal.loading) {
      setDeleteModal({ isOpen: false, paymentMethod: null, loading: false });
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('paymentMethods.header.title')}</h1>
        <p className="mt-2 text-sm text-gray-600">
          {t('paymentMethods.header.subtitle')}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">{t('paymentMethods.loading')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Liste des cartes */}
          {paymentMethods.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-medium text-gray-900">{t('paymentMethods.savedCards')}</h2>
              {paymentMethods.map((pm) => (
                <PaymentMethodCard
                  key={pm.id}
                  paymentMethod={pm}
                  onSetDefault={loadPaymentMethods}
                  onRemove={loadPaymentMethods}
                  onRequestDelete={handleRequestDelete}
                  t={t}
                />
              ))}
            </div>
          )}

          {/* Formulaire d'ajout */}
          {showAddForm ? (
            <div className="border border-gray-300 rounded-lg p-6 bg-white">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('paymentMethods.addModal.title')}</h2>
              <Elements stripe={stripePromise}>
                <AddCardForm
                  onSuccess={handleAddSuccess}
                  onCancel={() => setShowAddForm(false)}
                  t={t}
                />
              </Elements>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 hover:bg-gray-50 transition"
            >
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span className="mt-2 block text-sm font-medium text-gray-900">
                {t('paymentMethods.addCard')}
              </span>
            </button>
          )}

          {paymentMethods.length === 0 && !showAddForm && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('paymentMethods.empty.title')}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('paymentMethods.empty.description')}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg
            className="h-5 w-5 text-blue-400 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">{t('paymentMethods.info.title')}</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                {t('paymentMethods.info.secureStorage')}. {t('paymentMethods.info.defaultCard')}.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        cardInfo={deleteModal.paymentMethod?.card || { brand: '', last4: '' }}
        loading={deleteModal.loading}
        t={t}
      />
    </div>
  );
}
